import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { Logger } from '../ports'
import { extractMentionIds } from '../protocol/mentionText'
import { Chat, MessageReceivedEvent, SentMessage } from '../protocol/types'
import { describeChatError, fold, isErr, isOk } from '../result'
import { ChatApi } from '../transport/api'
import { ContactsCache } from './ContactsCache'

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error'

// A preview line needs the author's name and the names inside mention tokens —
// missing either renders a placeholder in the inbox row.
function previewContactIds(message: SentMessage): string[] {
  return [
    message.authorId,
    ...(message.text ? extractMentionIds(message.text) : []),
  ]
}

export class InboxStore {
  public chats: Chat[] = []
  public archivedChats: Chat[] = []
  public lastMessageByChannel = observable.map<string, SentMessage>()
  public unreadByChannel = observable.map<string, number>()
  public onlineUserIds = observable.set<string>()
  public status: LoadStatus = 'idle'
  public searchKey = ''

  private refreshInFlight: Promise<void> = null
  private needsAnotherRefresh = false

  public constructor(
    private readonly userId: string,
    private readonly api: ChatApi,
    private readonly contacts: ContactsCache,
    private readonly logger: Logger,
  ) {
    makeObservable(this, {
      chats: observable.ref,
      archivedChats: observable.ref,
      status: observable,
      searchKey: observable,
      setSearchKey: action,
      sortedChats: computed,
      sortedArchivedChats: computed,
    })
  }

  public setSearchKey = (value: string) => {
    this.searchKey = value
  }

  public get sortedChats(): Chat[] {
    const searchKey = this.searchKey.trim().toLowerCase()

    return this.chats
      .filter(chat => !chat.isArchived)
      .filter(chat =>
        searchKey ? chat.name.toLowerCase().includes(searchKey) : true,
      )
      .sort((c1, c2) => this.lastActivity(c2) - this.lastActivity(c1))
  }

  public get sortedArchivedChats(): Chat[] {
    return [...this.archivedChats].sort(
      (c1, c2) => this.lastActivity(c2) - this.lastActivity(c1),
    )
  }

  public directChatRecipientId(chat: Chat): string | undefined {
    return chat.memberIds.find(id => id !== this.userId)
  }

  // Matching on the counterpart, not on membership: every direct chat contains
  // the current user, so a membership check would match everything for a
  // self-lookup.
  public findById(channelId: string): Chat | undefined {
    const isMatch = (chat: Chat) => chat.id === channelId
    return this.chats.find(isMatch) ?? this.archivedChats.find(isMatch)
  }

  public findDirectChat(recipientId: string): Chat | undefined {
    const isMatch = (chat: Chat) =>
      !chat.isGroup && this.directChatRecipientId(chat) === recipientId
    return this.chats.find(isMatch) || this.archivedChats.find(isMatch)
  }

  public isOnline(userId: string): boolean {
    return this.onlineUserIds.has(userId)
  }

  public unreadCount(chatId: string): number {
    return this.unreadByChannel.get(chatId) ?? 0
  }

  public clearUnread = (channelId: string) => {
    runInAction(() => {
      this.unreadByChannel.set(channelId, 0)
    })
  }

  public async refresh(): Promise<void> {
    if (this.refreshInFlight) {
      this.needsAnotherRefresh = true
      return this.refreshInFlight
    }

    this.refreshInFlight = this.doRefresh()
    try {
      await this.refreshInFlight
    } finally {
      this.refreshInFlight = null
    }

    if (this.needsAnotherRefresh) {
      this.needsAnotherRefresh = false
      return this.refresh()
    }
  }

  private async doRefresh(): Promise<void> {
    runInAction(() => {
      if (this.status !== 'ready') {
        this.status = 'loading'
      }
    })

    const [inbox, archived] = await Promise.all([
      this.api.getInbox(this.userId),
      this.api.getArchivedChats(this.userId),
    ])

    if (isErr(inbox)) {
      this.logger.warn(`inbox load: ${describeChatError(inbox.error)}`)
      runInAction(() => {
        if (this.status !== 'ready') {
          this.status = 'error'
        }
      })
      return
    }
    if (isOk(inbox)) {
      runInAction(() => {
        this.chats = inbox.value
        if (isOk(archived)) {
          this.archivedChats = archived.value
        }
        this.status = 'ready'
      })

      await Promise.all([
        this.loadLastMessages([...inbox.value, ...this.archivedChats]),
        this.loadUnreadCounts(),
      ])
    }
  }

  // The server omits zero-count channels, so a full replace is what clears
  // pills for chats read elsewhere since the last refresh.
  private async loadUnreadCounts(): Promise<void> {
    const result = await this.api.getUnreadCounts(this.userId)
    fold(
      result,
      counts => {
        runInAction(() => {
          this.unreadByChannel.replace(
            counts.map(({ channelId, count }): [string, number] => [
              channelId,
              count,
            ]),
          )
        })
      },
      error => this.logger.warn(`unread counts: ${describeChatError(error)}`),
    )
  }

  private async loadLastMessages(chats: readonly Chat[]): Promise<void> {
    const ids = chats
      .map(chat => chat.lastMessageId)
      .filter((id): id is string => !!id)

    const known = new Set(
      [...this.lastMessageByChannel.values()].map(message => message.id),
    )
    const missingIds = ids.filter(id => !known.has(id))
    if (!missingIds.length) {
      return
    }

    const result = await this.api.getMessagesByIds(missingIds)
    fold(
      result,
      messages => {
        runInAction(() => {
          messages.forEach(message => {
            const present = this.lastMessageByChannel.get(message.channelId)
            if (!present || message.createdAt >= present.createdAt) {
              this.lastMessageByChannel.set(message.channelId, message)
            }
          })
        })

        this.contacts.ensureLoaded(messages.flatMap(previewContactIds))
      },
      error => this.logger.warn(`last messages: ${describeChatError(error)}`),
    )
  }

  public onMessageReceived = ({ message }: MessageReceivedEvent) => {
    this.contacts.ensureLoaded(previewContactIds(message))
    runInAction(() => {
      const present = this.lastMessageByChannel.get(message.channelId)
      if (!present || message.createdAt >= present.createdAt) {
        this.lastMessageByChannel.set(message.channelId, message)
      }
      // Own messages from another device are already read by definition.
      if (message.authorId !== this.userId) {
        this.unreadByChannel.set(
          message.channelId,
          this.unreadCount(message.channelId) + 1,
        )
      }
    })

    const isKnownChannel =
      this.chats.some(chat => chat.id === message.channelId) ||
      this.archivedChats.some(chat => chat.id === message.channelId)
    if (!isKnownChannel) {
      this.refresh()
    }
  }

  public onChatCreated = (chat: Chat) => {
    this.upsert(chat)
  }

  public onChatUpdated = (chat: Chat) => {
    const isMember = chat.memberIds.includes(this.userId)

    runInAction(() => {
      this.archivedChats = this.archivedChats.filter(c => c.id !== chat.id)
      if (isMember && chat.isArchived) {
        this.archivedChats = [...this.archivedChats, chat]
      }
    })

    if (isMember && !chat.isArchived) {
      this.upsert(chat)
    } else {
      runInAction(() => {
        this.chats = this.chats.filter(c => c.id !== chat.id)
      })
    }
  }

  public onContactJoined = (userId: string) => {
    runInAction(() => this.onlineUserIds.add(userId))
  }

  public onContactLeft = (userId: string) => {
    runInAction(() => this.onlineUserIds.delete(userId))
  }

  public setOnlineContacts(userIds: readonly string[]) {
    runInAction(() => {
      this.onlineUserIds.replace(userIds)
    })
  }

  private upsert(chat: Chat) {
    runInAction(() => {
      const index = this.chats.findIndex(c => c.id === chat.id)
      const next = [...this.chats]
      if (index === -1) {
        next.push(chat)
      } else {
        next[index] = chat
      }
      this.chats = next
    })
  }

  private lastActivity(chat: Chat): number {
    return this.lastMessageByChannel.get(chat.id)?.createdAt ?? chat.createdAt
  }
}
